import { useEffect } from "react";
import { useI18n } from "./i18n";

/**
 * Set the browser tab title, <meta name="description">, canonical URL and
 * Open Graph tags for the current page. Pass a `pick`-style object
 * { da, en, es } for each field. Also keeps <html lang> in sync.
 *
 * When a CMS-provided `seo` payload (`{title:{en,da,es}, description:{en,da,es}}`)
 * is passed via `opts.cmsSeo`, its non-empty values override the static
 * `titleByLang`/`descriptionByLang` — this is how every page's SEO becomes
 * editable from the CMS without touching code.
 */
export function usePageSEO(titleByLang, descriptionByLang, opts) {
  const { lang } = useI18n();
  useEffect(() => {
    document.documentElement.lang = lang || "da";
    const cmsSeo = opts?.cmsSeo || null;
    const cmsTitle = cmsSeo && cmsSeo.title && (cmsSeo.title[lang] || cmsSeo.title.en);
    const cmsDesc  = cmsSeo && cmsSeo.description && (cmsSeo.description[lang] || cmsSeo.description.en);
    const title = (cmsTitle && cmsTitle.trim()) || titleByLang?.[lang] || titleByLang?.en || "DANAmark";
    document.title = title;
    const desc = (cmsDesc && cmsDesc.trim()) || descriptionByLang?.[lang] || descriptionByLang?.en || "";
    setMeta("name", "description", desc);

    // Canonical URL
    const canonical = (opts && opts.canonical)
      || (typeof window !== "undefined" ? window.location.origin + window.location.pathname : "");
    setLink("canonical", canonical);

    // Open Graph
    const ogImage = (opts && opts.ogImage)
      || (typeof window !== "undefined" ? window.location.origin + "/images/og-default.jpg" : "");
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:site_name", "DANAmark");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", ogImage);
  }, [lang, titleByLang, descriptionByLang, opts]);
}

function setMeta(attr, key, value) {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value || "");
}

function setLink(rel, value) {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", value || "");
}

const T = (da, en, es) => ({ da, en, es });

export const SEO = {
  home: {
    title: T(
      "DANAmark | Danmark og Spanien i handling",
      "DANAmark | Denmark and Spain in action",
      "DANAmark | Dinamarca y España en acción"
    ),
    description: T(
      "Dansk forening, der forbinder Danmark og Spanien gennem fællesskab, kultur og sport.",
      "Danish volunteer-led association connecting Denmark and Spain through community, culture and sport.",
      "Asociación danesa impulsada por voluntarios que conecta Dinamarca y España a través de comunidad, cultura y deporte."
    ),
  },
  mission: {
    title: T("Vores historie | DANAmark", "Our Story | DANAmark", "Nuestra historia | DANAmark"),
    description: T(
      "Fra DANA i Valencia til Social Run i Fælledparken. Sådan opstod DANAmark.",
      "From the DANA in Valencia to Social Run in Fælledparken. How DANAmark began.",
      "De la DANA en Valencia a la Social Run en Fælledparken. Cómo nació DANAmark."
    ),
  },
  events: {
    title: T("Events | DANAmark", "Events | DANAmark", "Eventos | DANAmark"),
    description: T(
      "Fortidens og fremtidens DANAmark-oplevelser mellem Danmark og Spanien.",
      "Past and upcoming DANAmark experiences between Denmark and Spain.",
      "Experiencias pasadas y próximas de DANAmark entre Dinamarca y España."
    ),
  },
  socialRun: {
    title: T(
      "Social Run Copenhagen 2026 | DANAmark",
      "Social Run Copenhagen 2026 | DANAmark",
      "Social Run Copenhagen 2026 | DANAmark"
    ),
    description: T(
      "6. september 2026 · Fælledparken · 5 km. Fællesskab, kultur og forbindelse mellem Danmark og Spanien.",
      "6 September 2026 · Fælledparken · 5 km. Community, culture and connection between Denmark and Spain.",
      "6 de septiembre de 2026 · Fælledparken · 5 km. Comunidad, cultura y conexión entre Dinamarca y España."
    ),
  },
  impact: {
    title: T("Vores impact | DANAmark", "Our Impact | DANAmark", "Nuestro impacto | DANAmark"),
    description: T(
      "Valencia 2024, Social Run 2026, RENACE Ávila og vejen fremad. Sådan omsætter vi ideer til handling.",
      "Valencia 2024, Social Run 2026, RENACE Ávila and the road ahead. How we turn ideas into action.",
      "Valencia 2024, Social Run 2026, RENACE Ávila y el camino por delante. Cómo convertimos las ideas en acción."
    ),
  },
  join: {
    title: T("Bliv medlem | DANAmark", "Become a Member | DANAmark", "Hazte socio | DANAmark"),
    description: T(
      "Bliv medlem, frivillig, samarbejdspartner eller støtte af DANAmark.",
      "Join DANAmark as a member, volunteer, collaborator or supporter.",
      "Únete a DANAmark como socio, voluntario, colaborador o apoyo."
    ),
  },
  about: {
    title: T("Om DANAmark", "About DANAmark", "Sobre DANAmark"),
    description: T(
      "Dansk forening drevet af frivillige. CVR 45188981, København.",
      "Danish volunteer-led association. CVR 45188981, Copenhagen.",
      "Asociación danesa impulsada por voluntarios. CVR 45188981, Copenhague."
    ),
  },
  transparency: {
    title: T("Åbenhed | DANAmark", "Transparency | DANAmark", "Transparencia | DANAmark"),
    description: T(
      "DANAmark dokumenterer indsamling, udgifter og endelige donationer.",
      "DANAmark documents proceeds, event expenses and final donations.",
      "DANAmark documenta la recaudación, los gastos y las donaciones finales."
    ),
  },
  registration: {
    title: T(
      "Social Run for Spain | DANAmark",
      "Social Run for Spain | DANAmark",
      "Social Run for Spain | DANAmark"
    ),
    description: T(
      "Se hvad der skete til Social Run for Spain den 6. september 2026 i Fælledparken.",
      "See what happened at Social Run for Spain on 6 September 2026 in Fælledparken.",
      "Mira lo que pasó en la Social Run for Spain el 6 de septiembre de 2026 en Fælledparken."
    ),
  },
  raffle: {
    title: T(
      "DANAmark T-shirt | DANAmark",
      "DANAmark T-shirt | DANAmark",
      "Camiseta DANAmark | DANAmark"
    ),
    description: T(
      "Køb DANAmark T-shirten for 150 kr. Vælg dine størrelser, og betal sikkert online.",
      "Buy the DANAmark T-shirt for 150 DKK. Choose your sizes and pay securely online.",
      "Compra la camiseta DANAmark por 150 DKK. Elige tus tallas y paga de forma segura en línea."
    ),
  },
  shirts: {
    title: T("DANAmark T-shirts", "DANAmark T-shirts", "Camisetas DANAmark"),
    description: T(
      "DANAmark T-shirt til 150 DKK. Størrelser XS til XXL.",
      "DANAmark T-shirt for 150 DKK. Sizes XS to XXL.",
      "Camiseta DANAmark por 150 DKK. Tallas XS a XXL."
    ),
  },
};
